import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { StatusBadge } from '@/components/ui/status-badge';
import { ArrowLeft, Clock, BookOpen, Calendar } from 'lucide-react';
import { testApi } from '@/lib/api';
import { Test } from '@/types';
import { formatLocalDate, formatLocalDateTime } from '../utils/dateUtils';

const TestDetailPage = () => {
  const { testId } = useParams<{ testId: string }>();
  const [test, setTest] = useState<Test | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchTest = async () => {
      if (!testId) {
        setError('Test ID is required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const testData = await testApi.getActualTestById(testId);
        setTest(testData);
      } catch (err) {
        console.error('Failed to fetch test:', err);
        setError('Failed to load test details');
      } finally {
        setLoading(false);
      }
    };

    fetchTest();
  }, [testId]);

  const getStatusBadge = (test: Test) => {
    // Use displayStatus if available, otherwise fall back to status
    const statusToShow = test.displayStatus || test.status;

    if (!statusToShow) {
      return <StatusBadge status="unknown" />;
    }

    return <StatusBadge status={statusToShow.toLowerCase()} />;
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="icon" asChild>
            <Link to="/tests">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <h1 className="text-3xl font-bold tracking-tight">Test Details</h1>
        </div>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading test details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !test) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="icon" asChild>
            <Link to="/tests">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <h1 className="text-3xl font-bold tracking-tight">Test Details</h1>
        </div>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-8">
              <p className="text-destructive">{error || 'Test not found'}</p>
              <Button asChild className="mt-4">
                <Link to="/tests">Back to Tests</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="outline" size="icon" asChild>
          <Link to="/tests">
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <h1 className="text-3xl font-bold tracking-tight">Test Details</h1>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Test Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-sm font-medium text-muted-foreground">Title</h3>
              <p className="text-lg font-medium">{test.title || 'Untitled Test'}</p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-muted-foreground">Status</h3>
              <div className="mt-1">
                {getStatusBadge(test)}
              </div>
            </div>
            <div className="md:col-span-2">
              <h3 className="text-sm font-medium text-muted-foreground">Description</h3>
              <p className="text-lg">{test.description || 'No description provided'}</p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-muted-foreground">Duration</h3>
              <div className="flex items-center text-lg">
                <Clock className="mr-2 h-4 w-4 text-muted-foreground" />
                {test.duration || 0} minutes
              </div>
            </div>
            <div>
              <h3 className="text-sm font-medium text-muted-foreground">Subject</h3>
              <div className="flex items-center text-lg">
                <BookOpen className="mr-2 h-4 w-4 text-muted-foreground" />
                {test.subjectName || 'N/A'}
              </div>
            </div>
            <div>
              <h3 className="text-sm font-medium text-muted-foreground">Total Questions</h3>
              <p className="text-lg font-medium">{test.totalQuestions || 0}</p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-muted-foreground">Created Date</h3>
              <div className="flex items-center text-lg">
                <Calendar className="mr-2 h-4 w-4 text-muted-foreground" />
                {test.createdAt ? formatLocalDate(test.createdAt) : 'N/A'}
              </div>
            </div>
            {/* Schedule Information */}
            {(test.startDate || test.scheduledDate) && (
              <div className="md:col-span-2">
                <h3 className="text-sm font-medium text-muted-foreground">Schedule Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                  {test.startDate && (
                    <div>
                      <h4 className="text-xs font-medium text-muted-foreground">Start Time</h4>
                      <div className="flex items-center text-sm">
                        <Calendar className="mr-2 h-3 w-3 text-muted-foreground" />
                        {formatLocalDateTime(test.startDate)}
                      </div>
                    </div>
                  )}
                  {test.endDate && (
                    <div>
                      <h4 className="text-xs font-medium text-muted-foreground">End Time</h4>
                      <div className="flex items-center text-sm">
                        <Calendar className="mr-2 h-3 w-3 text-muted-foreground" />
                        {formatLocalDateTime(test.endDate)}
                      </div>
                    </div>
                  )}
                  {test.scheduledDate && !test.startDate && (
                    <div>
                      <h4 className="text-xs font-medium text-muted-foreground">Scheduled Date</h4>
                      <div className="flex items-center text-sm">
                        <Calendar className="mr-2 h-3 w-3 text-muted-foreground" />
                        {formatLocalDateTime(test.scheduledDate)}
                      </div>
                    </div>
                  )}
                  {test.publishedAt && (
                    <div>
                      <h4 className="text-xs font-medium text-muted-foreground">Published At</h4>
                      <div className="flex items-center text-sm">
                        <Calendar className="mr-2 h-3 w-3 text-muted-foreground" />
                        {formatLocalDateTime(test.publishedAt)}
                      </div>
                    </div>
                  )}
                  {test.lockTime && (
                    <div>
                      <h4 className="text-xs font-medium text-muted-foreground">Lock Time</h4>
                      <div className="flex items-center text-sm">
                        <Calendar className="mr-2 h-3 w-3 text-muted-foreground" />
                        {formatLocalDate(test.lockTime)}
                      </div>
                    </div>
                  )}
                  {test.onDemandTestStartDate && (
                    <div>
                      <h4 className="text-xs font-medium text-muted-foreground">Available From</h4>
                      <div className="flex items-center text-sm">
                        <Calendar className="mr-2 h-3 w-3 text-muted-foreground" />
                        {formatLocalDate(test.onDemandTestStartDate)}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            {test.difficultyDistribution && (
              <div className="md:col-span-2">
                <h3 className="text-sm font-medium text-muted-foreground">Difficulty Distribution</h3>
                <div className="flex gap-4 mt-2">
                  <div className="text-sm">
                    <span className="font-medium">Easy:</span> {test.difficultyDistribution.easyPercentage}%
                  </div>
                  <div className="text-sm">
                    <span className="font-medium">Medium:</span> {test.difficultyDistribution.mediumPercentage}%
                  </div>
                  <div className="text-sm">
                    <span className="font-medium">Hard:</span> {test.difficultyDistribution.hardPercentage}%
                  </div>
                </div>
              </div>
            )}
            {test.kitNumbers && test.kitNumbers.length > 0 && (
              <div className="md:col-span-2">
                <h3 className="text-sm font-medium text-muted-foreground">Kit Numbers</h3>
                <div className="flex gap-2 mt-2">
                  {test.kitNumbers.map((kit, index) => (
                    <Badge key={index} variant="outline">{kit}</Badge>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="mt-8">
            <h3 className="text-lg font-medium mb-4">Assignment Statistics</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 border rounded-lg">
                <h4 className="text-sm font-medium text-muted-foreground">Students Assigned</h4>
                <p className="text-2xl font-bold">{test.assignedStudentCount || 0}</p>
              </div>
              <div className="p-4 border rounded-lg">
                <h4 className="text-sm font-medium text-muted-foreground">Students Completed</h4>
                <p className="text-2xl font-bold">{test.completedStudentCount || 0}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TestDetailPage;
